import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Lock, ArrowRight, Crown } from 'lucide-react';
import { getTierConfig } from '../constants/tiers';

const FEATURE_INFO = {
  // Elite-only killer features
  launchVolumeForecaster: {
    name: 'Launch Volume Forecaster',
    description: 'Forecast realistic organic volume for a token launch based on real market comparables — similar projects, market caps, and exchange listings. Coming soon.',
    icon: '📈',
  },
  aiRiskAnalyst: {
    name: 'AI Risk Analyst',
    description: 'Chat with your trading data using haythix AI. Ask questions in plain English and get detailed analysis, anomaly explanations, and actionable recommendations.',
    icon: '🧠',
  },
  launchRoadmap: {
    name: 'Launch Roadmap Planner',
    description: 'Plan token launches across phased timelines with real milestone tracking — marketing pushes, exchange listings, audits, partnerships, treasury moves, community goals. Replaces the wash-trading "Campaign Scheduler".',
    icon: '📅',
  },
  volumeHealthWidget: {
    name: 'Volume Health Widget',
    description: 'Embeddable SVG badge showing your token\'s health score (0-100). Build trust with investors by displaying real volume metrics on your website.',
    icon: '📊',
  },
  competitorTracker: {
    name: 'Competitor Activity Tracker',
    description: 'Monitor competing tokens\' volume patterns, detect bot activity, and benchmark your performance with suspicion scoring.',
    icon: '🔍',
  },
  tokenomicsAdvisor: {
    name: 'AI Tokenomics Advisor',
    description: 'Upload your tokenomics PDF — our AI analyzes distribution schedules, identifies risks, and generates a custom 30/60/90-day strategy.',
    icon: '📄',
  },
  // Pro features
  aiStrategyGen: {
    name: 'AI Strategy Generator',
    description: 'Describe your trading strategy in plain English and let AI build the complete rule-based automation for you.',
    icon: '🤖',
  },
  arbitrageDetector: {
    name: 'Cross-Exchange Arbitrage',
    description: 'Detect and exploit price differences across multiple exchanges and chains automatically.',
    icon: '⚖️',
  },
  portfolioRebalancing: {
    name: 'Portfolio Rebalancing',
    description: 'Automatically rebalance your portfolio across tokens and chains to maintain target allocations.',
    icon: '🔄',
  },
  backtesting: {
    name: 'Backtesting Engine',
    description: 'Test your strategies against 7, 30, or 90 days of historical data before risking real capital.',
    icon: '📈',
  },
  advancedSignals: {
    name: 'Advanced AI Signals',
    description: 'Get AI-powered Buy/Sell/Hold signals with confidence scores, reasoning, and performance tracking.',
    icon: '📡',
  },
  // Trader features
  copyTrading: {
    name: 'Copy Trading',
    description: 'Follow top-performing traders on the leaderboard and automatically replicate their trades in real-time.',
    icon: '👥',
  },
  gasMonitor: {
    name: 'Gas Price Monitor',
    description: 'Monitor gas prices across chains and optimize trade timing to minimize transaction costs.',
    icon: '⛽',
  },
  botCloning: {
    name: 'Bot Cloning',
    description: 'Duplicate any bot configuration instantly with a clean ID and customizable settings.',
    icon: '📋',
  },
  fullMarketplace: {
    name: 'Full Strategy Marketplace',
    description: 'Access the complete strategy marketplace — browse, deploy, and clone advanced strategies from top creators.',
    icon: '🏪',
  },
  webhookAlerts: {
    name: 'Webhook Alerts',
    description: 'Send real-time trade and signal alerts to your own endpoints, Slack, or custom integrations.',
    icon: '🔔',
  },
};

export default function UpgradeModal({ featureKey, onClose }) {
  const navigate = useNavigate();
  const info = FEATURE_INFO[featureKey] || {
    name: 'Premium Feature',
    description: 'This feature requires a higher tier to access.',
    icon: '✨',
  };

  const requiredTier = getRequiredTierForFeature(featureKey);
  const tierCfg = getTierConfig(requiredTier);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-2xl bg-[#0d1017] border border-dark-600 shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 text-gray-500 hover:text-white transition-colors">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className={`px-6 pt-8 pb-6 text-center ${
          requiredTier === 'elite'
            ? 'bg-gradient-to-b from-amber-500/10 to-transparent'
            : requiredTier === 'pro'
            ? 'bg-gradient-to-b from-violet-500/10 to-transparent'
            : 'bg-gradient-to-b from-cyan-500/10 to-transparent'
        }`}>
          <div className="text-4xl mb-3">{info.icon}</div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lock className={`w-4 h-4 ${tierCfg.color}`} />
            <span className={`text-xs font-bold tracking-widest uppercase ${tierCfg.color}`}>
              {tierCfg.label} Feature
            </span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{info.name}</h2>
          <p className="text-sm text-gray-400 leading-relaxed">{info.description}</p>
        </div>

        {/* Pricing card */}
        <div className="mx-6 mb-5 px-4 py-4 rounded-xl bg-dark-700/50 border border-dark-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tierCfg.bgColor}`}>
                <Crown className={`w-4 h-4 ${tierCfg.color}`} />
              </div>
              <div>
                <p className={`text-sm font-bold ${tierCfg.color}`}>{tierCfg.label}</p>
                <p className="text-[11px] text-gray-500">Unlock this feature</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white font-bold text-lg">${tierCfg.annualPrice}<span className="text-xs text-gray-500 font-normal">/mo</span></p>
              <p className="text-[11px] text-gray-500">billed annually</p>
            </div>
          </div>
        </div>

        {/* CTA buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-medium text-gray-400 border border-dark-600 hover:text-white hover:border-dark-500 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={() => { onClose(); navigate(`/register?plan=${requiredTier}`); }}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
              requiredTier === 'elite'
                ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 hover:from-amber-300 hover:to-orange-300'
                : 'bg-white text-gray-900 hover:bg-gray-100'
            }`}
          >
            Upgrade Now <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Find which tier first enables a feature */
function getRequiredTierForFeature(featureKey) {
  const { TIER_ORDER, TIER_CONFIG } = require('../constants/tiers');
  for (const id of TIER_ORDER) {
    if (TIER_CONFIG[id]?.features[featureKey]) return id;
  }
  return 'elite';
}
